import { ArrowSquareOut, DownloadSimple, GearSix, List, Plus, SignOut, Trash, UploadSimple, X } from "@phosphor-icons/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { planHref, readPlanLocation, type HistoryRange, type PlanTab } from "@/lib/navigation";
import type { Instrument, Plan, User } from "@/lib/types";
import { PlanWorkspace } from "./PlanWorkspace";
import { ThemeToggle } from "./ThemeToggle";

const AdminPanel = lazy(() => import("./AdminPanel").then((module) => ({ default: module.AdminPanel })));

function downloadJson(payload: unknown) {
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = `Universal_Planner_${new Date().toISOString().slice(0, 10)}.json`;
  link.click();
  window.setTimeout(() => URL.revokeObjectURL(link.href), 0);
}

function CreatePlan({ instruments, onCreate, onClose }: { instruments: Instrument[]; onCreate: (name: string, instrumentId: string) => Promise<void>; onClose: () => void }) {
  const [name, setName] = useState("");
  const [instrumentId, setInstrumentId] = useState(instruments[0]?.id ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.currentTarget === event.target && onClose()}>
      <form className="modal" onSubmit={async (event) => { event.preventDefault(); setBusy(true); setError(""); try { await onCreate(name, instrumentId); } catch { setError("สร้างแผนไม่สำเร็จ ชื่ออาจซ้ำกับแผนเดิม"); setBusy(false); } }}>
        <div className="modal-head"><div><span className="eyebrow">New plan</span><h2>เริ่มแผนใหม่</h2></div><button type="button" className="icon-button" onClick={onClose} aria-label="ปิดหน้าต่าง"><X /></button></div>
        <label className="field"><span>ชื่อแผน</span><input autoFocus required maxLength={80} value={name} onChange={(event) => setName(event.target.value)} placeholder="เช่น SNDK รอบกันยายน" /></label>
        <label className="field"><span>หุ้น</span><select required value={instrumentId} onChange={(event) => setInstrumentId(event.target.value)}>{instruments.map((instrument) => <option value={instrument.id} key={instrument.id}>{instrument.symbol} · {instrument.displayName}</option>)}</select></label>
        {error ? <div className="error-banner">{error}</div> : null}
        <div className="modal-actions"><button type="button" className="ghost-button" onClick={onClose}>ยกเลิก</button><button className="primary-button" disabled={busy || !instrumentId}>{busy ? "กำลังสร้าง…" : "สร้างแผน"}</button></div>
      </form>
    </div>
  );
}

export function AppShell({ user, onLogout }: { user: User; onLogout: () => void }) {
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const plansQuery = useQuery({ queryKey: ["plans"], queryFn: api.plans });
  const instrumentsQuery = useQuery({ queryKey: ["instruments"], queryFn: api.instruments });
  const [createOpen, setCreateOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const importInput = useRef<HTMLInputElement>(null);
  const plans = plansQuery.data?.plans ?? [];
  const planLocation = readPlanLocation(location.pathname, location.search);
  const view = location.pathname === "/admin" && user.role === "OWNER" ? "admin" : "plans";
  const selected = plans.find((plan) => plan.id === planLocation.planId) ?? plans[0] ?? null;

  useEffect(() => {
    if (plansQuery.isLoading) return;
    if (view === "admin") {
      if (location.search) navigate("/admin", { replace: true });
      return;
    }
    const target = planHref(selected?.id ?? null, planLocation.tab, planLocation.range);
    if (`${location.pathname}${location.search}` !== target) navigate(target, { replace: true });
  }, [location.pathname, location.search, navigate, planLocation.range, planLocation.tab, plansQuery.isLoading, selected?.id, view]);

  const openPlanLocation = (planId: string, tab: PlanTab = planLocation.tab, range: HistoryRange = planLocation.range) => {
    navigate(planHref(planId, tab, range));
  };

  const selectPlan = (plan: Plan) => {
    openPlanLocation(plan.id, view === "admin" ? "planner" : planLocation.tab);
    setDrawerOpen(false);
    api.openPlan(plan.id).catch(() => undefined);
  };
  const updateCache = (plan: Plan) => {
    queryClient.setQueryData<{ plans: Plan[] }>(["plans"], (current) => current ? { plans: current.plans.map((item) => item.id === plan.id ? plan : item) } : current);
  };
  const insertPlan = (plan: Plan) => {
    queryClient.setQueryData<{ plans: Plan[] }>(["plans"], (current) => ({ plans: [plan, ...(current?.plans ?? [])] }));
    openPlanLocation(plan.id, "planner");
  };
  const removePlan = async (plan: Plan) => {
    if (!window.confirm(`ลบแผน “${plan.name}” หรือไม่?`)) return;
    await api.deletePlan(plan.id, plan.version);
    queryClient.setQueryData<{ plans: Plan[] }>(["plans"], (current) => ({ plans: (current?.plans ?? []).filter((item) => item.id !== plan.id) }));
  };
  const createPlan = async (name: string, instrumentId: string) => {
    const response = await api.createPlan({ name, instrumentId });
    insertPlan(response.plan);
    setCreateOpen(false);
  };
  const importLegacy = async (file: File) => {
    const payload = JSON.parse(await file.text()) as unknown;
    const result = await api.importLegacy(payload);
    await queryClient.invalidateQueries({ queryKey: ["plans"] });
    window.alert(`Import สำเร็จ ${result.imported.length} แผน${result.skipped.length ? ` · ข้าม ${result.skipped.length} แผนเพราะยังไม่มี ticker` : ""}`);
  };

  const sidebar = (
    <aside className={`sidebar ${drawerOpen ? "open" : ""}`}>
      <div className="brand-row"><div className="brand-mark">UP</div><div><strong>Universal Planner</strong><span>Private workspace</span></div><button className="icon-button mobile-only" onClick={() => setDrawerOpen(false)} aria-label="ปิดรายการแผน"><X /></button></div>
      <button className="new-plan-button" onClick={() => setCreateOpen(true)} disabled={(instrumentsQuery.data?.instruments.length ?? 0) === 0}><Plus /> สร้างแผนใหม่</button>
      <nav className="plan-nav" aria-label="แผนของฉัน"><span className="nav-label">MY PLANS</span>{plans.map((plan) => <button key={plan.id} className={selected?.id === plan.id && view === "plans" ? "active" : ""} onClick={() => selectPlan(plan)}><span className="ticker-mini">{plan.instrument.symbol}</span><span><strong>{plan.name}</strong><small>แก้ไข {new Date(plan.updatedAt).toLocaleDateString("th-TH")}</small></span></button>)}{!plansQuery.isLoading && plans.length === 0 ? <p className="empty-nav">ยังไม่มีแผน เริ่มจากปุ่มด้านบน</p> : null}</nav>
      <div className="sidebar-bottom">
        {user.role === "OWNER" ? <button className={view === "admin" ? "active" : ""} onClick={() => { navigate("/admin"); setDrawerOpen(false); }}><GearSix /> Owner admin</button> : null}
        <button onClick={() => api.exportPlans().then(downloadJson)}><DownloadSimple /> Export cloud JSON</button>
        <button onClick={() => importInput.current?.click()}><UploadSimple /> Import legacy JSON</button>
        <input ref={importInput} hidden type="file" accept="application/json,.json" onChange={(event) => { const file = event.target.files?.[0]; if (file) importLegacy(file).catch(() => window.alert("Import ไม่สำเร็จ: ไฟล์ไม่ถูกต้อง")); event.target.value = ""; }} />
      </div>
      <div className="profile-row">{user.pictureUrl ? <img src={user.pictureUrl} alt="" /> : <span className="avatar-fallback">{user.displayName.slice(0, 1)}</span>}<div><strong>{user.displayName}</strong><small>{user.role === "OWNER" ? "Owner" : "Member"}</small></div><button className="icon-button" onClick={onLogout} aria-label="ออกจากระบบ"><SignOut /></button></div>
    </aside>
  );

  return (
    <div className="app-shell">
      {sidebar}
      {drawerOpen ? <div className="drawer-shade" onClick={() => setDrawerOpen(false)} /> : null}
      <div className="content-column">
        <header className="mobile-header"><button className="icon-button" onClick={() => setDrawerOpen(true)} aria-label="เปิดรายการแผน"><List /></button><div><strong>{selected?.name ?? "Universal Planner"}</strong><small>{selected?.instrument.symbol ?? "Private workspace"}</small></div><ThemeToggle /></header>
        <div className="desktop-theme"><ThemeToggle /></div>
        {view === "admin" ? <Suspense fallback={<main className="workspace"><div className="empty-state">กำลังเปิด Owner console…</div></main>}><AdminPanel /></Suspense> : selected ? <>
          <div className="plan-actions"><button className="ghost-button" onClick={() => api.duplicatePlan(selected.id).then(({ plan }) => insertPlan(plan))}><Plus /> Duplicate</button><button className="danger-button" onClick={() => removePlan(selected)}><Trash /> Delete</button></div>
          <PlanWorkspace
            plan={selected}
            tab={planLocation.tab}
            historyRange={planLocation.range}
            onTabChange={(tab) => openPlanLocation(selected.id, tab)}
            onHistoryRangeChange={(range) => openPlanLocation(selected.id, "history", range)}
            onSaved={updateCache}
            onReload={() => window.location.reload()}
            onDuplicate={insertPlan}
          />
        </> : plansQuery.isLoading ? <main className="workspace"><div className="empty-state">กำลังเปิดแผนล่าสุด…</div></main> : <main className="workspace"><section className="welcome-empty"><span className="eyebrow">Ready when you are</span><h1>สร้างแผนลงทุนแรกของคุณ</h1><p>เลือกหุ้นที่ owner เปิดใช้งาน แล้วระบบจะบันทึกทุกการแก้ไขไว้บน Cloud</p><button className="primary-button" onClick={() => setCreateOpen(true)} disabled={(instrumentsQuery.data?.instruments.length ?? 0) === 0}><Plus /> สร้างแผนใหม่</button>{(instrumentsQuery.data?.instruments.length ?? 0) === 0 ? <small>ยังไม่มีหุ้นที่เปิดใช้งาน กรุณาให้ owner เพิ่ม Instrument ก่อน</small> : null}</section></main>}
      </div>
      {createOpen ? <CreatePlan instruments={instrumentsQuery.data?.instruments ?? []} onCreate={createPlan} onClose={() => setCreateOpen(false)} /> : null}
    </div>
  );
}
